import { useState, useEffect } from 'react'
import { SquareArrowOutUpRight, X } from 'lucide-react'
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { EntryContext } from './types'
import { usePlayerCard } from './usePlayerCard'
import { headerColors } from '@/features/_shared/teamColor'
import { PlayerCardHeader } from './PlayerCardHeader'
import { Summary } from './Summary'
import { FullStatsTab } from './FullStatsTab'
import { HistoryTab } from './HistoryTab'
import { ManageTab } from './ManageTab'

type TabId = 'summary' | 'fullstats' | 'history' | 'manage'

interface Props {
  playerId: number | null
  entry: EntryContext
  defaultTab?: 'summary' | 'fullstats' | 'history'
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PlayerCard({ playerId, entry, defaultTab = 'summary', open, onOpenChange }: Props) {
  const [tab, setTab] = useState<TabId>(defaultTab)

  // Reset tab when the card opens with a new player or reopens
  useEffect(() => {
    if (open) setTab(defaultTab)
  }, [open, playerId]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: player, isLoading, isError } = usePlayerCard(playerId, {
    summary: tab === 'summary',
    history: tab === 'history',
    fullStats: tab === 'fullstats',
  })

  // Color the dialog controls with the header's contrast-guarded foreground +
  // translucent chip so they stay legible on any team color (incl. dark headers).
  const c = player ? headerColors(player.team?.colorPrimary, player.team?.colorSecondary) : null
  const ctrlChip = c?.fgIsDark
    ? 'bg-black/10 hover:bg-black/20 ring-black/15'
    : 'bg-white/20 hover:bg-white/30 ring-white/25'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        // Fixed height so the card stays the SAME size regardless of player /
        // active tab / pitcher-vs-batter / career length — otherwise the box
        // shrink-wraps its content and visibly jumps size. The inner scroll body
        // (below) absorbs the difference. `max-h-[90svh]` still caps it on short
        // screens (shorter than the fixed height).
        // Landscape phones (landscape-short): the fixed height + 90svh cap leaves
        // a small, header-heavy box marooned mid-screen, so pin a taller definite
        // height that fills the viewport (dvh — landscape Safari chrome is
        // minimal, so the svh top-clip concern that governs portrait doesn't bite
        // here) and let the scroll area take the space instead of the header.
        // Width: compact on mobile (viewport-capped anyway), but widened on
        // desktop (md:) so the Full Stats "scroll everything" tables — pitching
        // especially — fit with far less horizontal scroll. Height stays fixed.
        className="p-0 gap-0 overflow-hidden flex flex-col max-w-2xl md:max-w-4xl h-[680px] max-h-[90svh] landscape-short:h-[92dvh] landscape-short:max-h-[92dvh]"
      >
        <div className="absolute right-4 top-4 z-50 flex items-center gap-1.5">
          {/* "Open full page in a new tab" — the player's full /player/{id}/view
              page. Window chrome alongside Close, not a player action, so it lives
              in this corner cluster rather than the header's action slot. Shown on
              all sizes (sharing/opening a full page is useful on phones too). */}
          {playerId != null && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={`/player/${playerId}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open full page in a new tab"
                  className={cn(
                    // UI review 2026-06-11 #21: labeled pill (icon + "Full page"),
                    // not an icon-only glyph that reads as window chrome.
                    'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/60',
                    c ? cn('ring-1 ring-inset', ctrlChip) : 'opacity-70 hover:opacity-100',
                  )}
                  style={c ? { color: c.fg } : undefined}
                >
                  <SquareArrowOutUpRight className="h-3.5 w-3.5" />
                  Full page
                </a>
              </TooltipTrigger>
              <TooltipContent>Open full page (new tab)</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogClose
                aria-label="Close"
                className={cn(
                  'rounded p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-white/60',
                  c ? cn('ring-1 ring-inset', ctrlChip) : 'opacity-70 hover:opacity-100',
                )}
                style={c ? { color: c.fg } : undefined}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </TooltipTrigger>
            <TooltipContent>Close</TooltipContent>
          </Tooltip>
        </div>
        {isLoading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
        {!isLoading && (isError || !player) && (
          <div className="p-6">
            <DialogTitle className="text-base">Player</DialogTitle>
            <p className="mt-2 text-sm text-slate-500">We couldn't find that player.</p>
          </div>
        )}
        {player && (
          <>
            <PlayerCardHeader player={player} />
            <DialogTitle className="sr-only">{player.fullName}</DialogTitle>
            <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)} className="flex min-h-0 flex-1 flex-col bg-white">
              <TabsList className="h-auto justify-start gap-1 rounded-none border-b border-slate-200 bg-transparent px-4 pt-2">
                {/* Manage is commissioner/admin-only — appended only when the
                    server says the viewer can rename or reassign this player.
                    Hidden for retired players (UI review 2026-06-11 #24): there's
                    nothing to manage once retired, and it removes their rename path. */}
                {([
                  'summary',
                  'fullstats',
                  'history',
                  ...(player.canManage && !player.bio.retired ? (['manage'] as const) : []),
                ] as TabId[]).map((v) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    className="rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2 pt-1.5 text-sm font-semibold text-slate-500 shadow-none data-[state=active]:border-navy data-[state=active]:bg-transparent data-[state=active]:text-navy data-[state=active]:shadow-none"
                  >
                    {v === 'summary' ? 'Summary' : v === 'fullstats' ? 'Stats' : v === 'history' ? 'History' : 'Manage'}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                <TabsContent value="summary"><Summary player={player} entry={entry} /></TabsContent>
                <TabsContent value="fullstats"><FullStatsTab player={player} /></TabsContent>
                <TabsContent value="history"><HistoryTab player={player} /></TabsContent>
                {player.canManage && !player.bio.retired && <TabsContent value="manage"><ManageTab player={player} /></TabsContent>}
              </div>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
